import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { IImageGeneration, ImageGenerationStatus, IUserModel, PaginationQuery, ImageGenerationQuery, ImageGenerationType, PaginatedResponse } from '@packages/global';
import { imageGenerationApi } from '../../services/user/imageGeneration.api';
import StatusBadge from '../ImageGeneration/StatusBadge';
import Pagination from '../Pagination';

type TabType = 'all' | 'generation' | 'edit';

interface ImageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (image: IImageGeneration) => void;
  models: IUserModel[];
}

export default function ImageSelector({ isOpen, onClose, onSelect, models }: ImageSelectorProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [images, setImages] = useState<IImageGeneration[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedImage, setSelectedImage] = useState<IImageGeneration | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, page: 1, limit: 12 });

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const loadImages = async (page: number = 1) => {
    setLoading(true);
    try {
      let query: ImageGenerationQuery = {
        search: search || undefined,
        status: ImageGenerationStatus.COMPLETED
      }
      if (activeTab === 'generation') {
        query.imageType = ImageGenerationType.GENERATION;
      } else if (activeTab === 'edit') {
        query.imageType = ImageGenerationType.EDIT;
      }
      const queryData: PaginationQuery<ImageGenerationQuery> = {
        query: query,
        pagination: {
          page,
          limit: 15,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await imageGenerationApi.imageGenerationPageList(queryData);
      setImages(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载图片失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedImage(null);
      setCurrentPage(1);
      loadImages(1);
    }
  }, [isOpen, search, activeTab]);

  const handleSelect = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[90]">
      <div className="bg-bg-surface rounded-lg w-[800px] h-[640px] flex flex-col border border-border-subtle shadow-2xl">
        <div className="flex items-center p-4 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-gray-50">{t('common_ui:selector.image.title')}</h2>
          <div className="flex bg-bg-elevated rounded-lg p-1 ml-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.image.tabAll')}
            </button>
            <button
              onClick={() => setActiveTab('generation')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'generation'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.image.tabGeneration')}
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'edit'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.image.tabEdit')}
            </button>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common_ui:selector.image.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-80 pl-9 pr-4 py-1.5 border border-border-subtle rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-bg-form text-gray-50 placeholder-gray-500 text-sm"
              />
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <div className="text-6xl mb-4">🖼️</div>
              <p>{t('common_ui:selector.image.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  onClick={() => setSelectedImage(image)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${selectedImage?.id === image.id
                    ? 'border-blue-500 ring-2 ring-blue-400'
                    : 'border-border-subtle hover:border-border-default'
                    }`}
                >
                  <div className="aspect-square">
                    {('resultImageUrl' in image ? image.resultImageUrl : image.resultImageUrl) ? (
                      <img src={image.resultImageUrl} alt={t('common_ui:selector.image.imageAlt')} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
                        <span className="text-gray-500 text-2xl">🖼️</span>
                      </div>
                    )}
                  </div>

                  {/* {'status' in image && (
                    <div className="absolute top-2 right-2">
                      <StatusBadge status={image.status} />
                    </div>
                  )} */}

                  {selectedImage?.id === image.id && (
                    <div className="absolute top-2 left-2">
                      <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">✓</div>
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <div className="text-white text-xs">
                      <div className="text-gray-200 truncate">
                        {('width' in image && 'height' in image) ? `${image.width} × ${image.height}` : t('common_ui:selector.image.imageAlt')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="flex-1">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={loadImages}
              showInfo={false}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedImage}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:selector.image.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
